import React, { Component } from "react";
import DropdownMaster from "./DropdownMaster";
import TextField from "./TextField";
import CalendarIcon from "../icons/calendaricon";
import DatePicker from "react-datepicker";
import moment from "moment";
//Service import
import service from "../services";
import LoaderSvg from "../assets/images/loader.svg";

const PRODUCT_TYPE_CODE = {
  inperson: "1",
  online: "2",
};

// UI-facing dropdown options. "yes"/"no" both map to the backend's
// `checkmark` key (value true/false) — the backend has no separate
// Yes/No key, only a boolean checkmark.
const VALUE_TYPE_OPTIONS = [
  { _id: "dateRange", name: "Date Range" },
  { _id: "yes", name: "Yes" },
  { _id: "no", name: "No" },
  { _id: "other", name: "Other" },
];

const DATE_FORMAT = "YYYY-MM-DD";

class ProductCompareBenefits extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loaderFlag: false,
      // A parent that already has the benefits list can pass it in to skip the fetch.
      allBenefits: props.benefits || [],
      benefitsLoaded: !!props.benefits,
      benefitValues: {},
    };
    this.apiserve = new service();
  }

  componentDidMount = () => {
    if (this.props.benefits) {
      this.hydrateFromValue();
      return;
    }
    this.getAllBenefits();
    this.getBenefitsEvent();
    this.setState({ loaderFlag: true });
  };

  componentDidUpdate = (prevProps) => {
    // The product's saved benefits can arrive from the parent's own fetch
    // *after* this component already hydrated once (its own benefits-list
    // call is usually faster). Re-hydrate whenever the incoming value
    // actually changes — but skip echoes of our own emitChange (same
    // content, new array reference) to avoid looping.
    if (prevProps.value === this.props.value) return;
    if (!this.state.allBenefits.length) return;
    const incomingJSON = JSON.stringify(this.props.value || []);
    if (incomingJSON === this.lastEmittedJSON) return;
    this.hydrateFromValue();
  };

  componentWillUnmount = () => {
    if (this.sub) this.sub.unsubscribe();
  };

  getBenefitsEvent = () => {
    this.sub = this.apiserve.getevent().subscribe((response) => {
      switch (response.event) {
        case "ticketCms|benefits|getList":
          if (!response.error) {
            const allBenefits = response.data?.benefits || [];
            this.setState({ allBenefits }, () => this.hydrateFromValue());
          }
          this.setState({ loaderFlag: false, benefitsLoaded: true });
          break;
      }
    });
  };

  getAllBenefits = () => {
    this.apiserve.sendevent("ticketCms|benefits|getList", {});
  };

  hydrateFromValue = () => {
    const savedValues = this.props.value || [];
    const benefitValues = {};
    savedValues.forEach((saved) => {
      const entry = { uiOption: "", dateFrom: null, dateTo: null, otherText: "" };
      if (saved.key === "checkmark") entry.uiOption = saved.value ? "yes" : "no";
      if (saved.key === "other") {
        entry.uiOption = "other";
        entry.otherText = saved.value || "";
      }
      if (saved.key === "dateRange") {
        entry.uiOption = "dateRange";
        entry.dateFrom = saved.value?.from ? moment(saved.value.from, DATE_FORMAT).toDate() : null;
        entry.dateTo = saved.value?.to ? moment(saved.value.to, DATE_FORMAT).toDate() : null;
      }
      benefitValues[saved.benefitId] = entry;
    });
    // Re-emit so the parent's saved state always matches exactly what this
    // component renders — if a saved benefitId no longer matches a visible
    // benefit (or has no key), it's dropped from the outgoing payload too.
    this.setState({ benefitValues }, this.emitChange);
  };

  getVisibleBenefits = () => {
    const code = PRODUCT_TYPE_CODE[this.props.productType];
    if (!code) return [];
    return this.state.allBenefits.filter((benefit) => {
      if (benefit.hideBenefit) return false;
      const types = Array.isArray(benefit.productType) ? benefit.productType : [benefit.productType];
      return types.includes(code);
    });
  };

  getBenefitValue = (benefitId) => {
    return (
      this.state.benefitValues[benefitId] || {
        uiOption: "",
        dateFrom: null,
        dateTo: null,
        otherText: "",
      }
    );
  };

  updateBenefitValue = (benefitId, changes) => {
    const benefitValues = {
      ...this.state.benefitValues,
      [benefitId]: { ...this.getBenefitValue(benefitId), ...changes },
    };
    this.setState({ benefitValues }, this.emitChange);
  };

  emitChange = () => {
    if (!this.props.onChange) return;
    const data = [];
    this.getVisibleBenefits().forEach((benefit) => {
      const entry = this.getBenefitValue(benefit._id);
      if (!entry.uiOption) return;
      let key;
      let value;
      if (entry.uiOption === "yes" || entry.uiOption === "no") {
        key = "checkmark";
        value = entry.uiOption === "yes";
      } else if (entry.uiOption === "other") {
        key = "other";
        value = entry.otherText;
      } else if (entry.uiOption === "dateRange") {
        key = "dateRange";
        value = {
          from: entry.dateFrom ? moment(entry.dateFrom).format(DATE_FORMAT) : "",
          to: entry.dateTo ? moment(entry.dateTo).format(DATE_FORMAT) : "",
        };
      }
      data.push({ benefitId: benefit._id, key, value });
    });
    this.lastEmittedJSON = JSON.stringify(data);
    this.props.onChange(data);
  };

  onValueTypeChange = (benefitId) => (e) => {
    const uiOption = e.target.value;
    const changes = { uiOption };
    if (uiOption === "dateRange") {
      const current = this.getBenefitValue(benefitId);
      const today = new Date();
      if (!current.dateFrom) changes.dateFrom = today;
      if (!current.dateTo) changes.dateTo = today;
    }
    this.updateBenefitValue(benefitId, changes);
  };

  onOtherTextChange = (benefitId) => (e) => {
    this.updateBenefitValue(benefitId, { otherText: e.target.value });
  };

  onDateFromChange = (benefitId) => (date) => {
    this.updateBenefitValue(benefitId, { dateFrom: date });
  };

  onDateToChange = (benefitId) => (date) => {
    this.updateBenefitValue(benefitId, { dateTo: date });
  };

  render() {
    const visibleBenefits = this.getVisibleBenefits();
    // Show only the spinner until the benefits list arrives, so the heading and
    // "Show in Grid" appear together with the benefits.
    if (!this.state.benefitsLoaded) {
      return (
        <div className="product_mylib">
          <h3>Compare Benefits</h3>
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <img src={LoaderSvg} alt="Loading" style={{ width: 50, height: 50 }} />
          </div>
        </div>
      );
    }
    return (
      <div className="product_mylib">
        <h3>Compare Benefits</h3>
        <div className="field_row horizontal_field">
          <div className="field_row_inner">
            <label>Show in Grid</label>
            <div className="relative mt_10">
              <div className="checkbox_field_row">
                <div className="checkBox">
                  <input
                    type="checkbox"
                    id="showProductInCompareBenefits"
                    name="showProductInCompareBenefits"
                    checked={!!this.props.showProductInCompareBenefits}
                    onChange={(e) => this.props.onShowProductInCompareBenefitsChange && this.props.onShowProductInCompareBenefitsChange(e.target.checked)}
                  />
                  <label for="showProductInCompareBenefits" style={{ width: "auto" }}></label>
                </div>
              </div>
            </div>
          </div>
        </div>
        {!this.props.productType ? (
          <p>Select a Product Type to configure benefits.</p>
        ) : !visibleBenefits.length ? (
          <p>No benefits configured for this product type.</p>
        ) : (
          visibleBenefits.map((benefit) => {
            const benefitValue = this.getBenefitValue(benefit._id);
            return (
              <React.Fragment key={benefit._id}>
                <div className="field_row horizontal_field">
                  <div className="field_row_inner">
                    <label>{benefit.benefitName}</label>
                    <div className="form_group_countdown d_flex d_flex_align_center" style={{ maxWidth: 331, width: "100%" }}>
                      <DropdownMaster
                        name={`benefitValueType_${benefit._id}`}
                        onChange={this.onValueTypeChange(benefit._id)}
                        value={benefitValue.uiOption}
                        options={VALUE_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
                {benefitValue.uiOption === "other" && (
                  <div className="field_row horizontal_field">
                    <div className="field_row_inner">
                      <label style={{ visibility: "hidden" }}>{benefit.benefitName}</label>
                      <div style={{ maxWidth: 331, width: "100%" }}>
                        <TextField
                          type="text"
                          name={`benefitOtherText_${benefit._id}`}
                          value={benefitValue.otherText}
                          onChange={this.onOtherTextChange(benefit._id)}
                          placeholder="Enter detail"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {benefitValue.uiOption === "dateRange" && (
                  <div className="field_row horizontal_field">
                    <div className="field_row_inner">
                      <label style={{ visibility: "hidden" }}>{benefit.benefitName}</label>
                      <div className="d_flex d_flex_align_center">
                        <div>
                          <label>Start Date</label>
                          <div className="form_group_countdown benefit_date_field d_flex d_flex_align_center">
                            <TextField
                              type="text"
                              name={`benefitDateFrom_${benefit._id}`}
                              value={benefitValue.dateFrom ? moment(benefitValue.dateFrom).format("MM/DD/YYYY") : ""}
                              placeholder="mm / dd / yyyy"
                            />
                            <span className="calendar_img">
                              <DatePicker
                                selected={benefitValue.dateFrom}
                                onChange={this.onDateFromChange(benefit._id)}
                                customInput={
                                  <span className="calendar">
                                    <CalendarIcon />
                                  </span>
                                }
                              />
                            </span>
                          </div>
                        </div>
                        <div>
                          <label>End Date</label>
                          <div className="form_group_countdown benefit_date_field d_flex d_flex_align_center">
                            <TextField
                              type="text"
                              name={`benefitDateTo_${benefit._id}`}
                              value={benefitValue.dateTo ? moment(benefitValue.dateTo).format("MM/DD/YYYY") : ""}
                              placeholder="mm / dd / yyyy"
                            />
                            <span className="calendar_img">
                              <DatePicker
                                selected={benefitValue.dateTo}
                                onChange={this.onDateToChange(benefit._id)}
                                minDate={benefitValue.dateFrom}
                                customInput={
                                  <span className="calendar">
                                    <CalendarIcon />
                                  </span>
                                }
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    );
  }
}

export default ProductCompareBenefits;
